import React, { useCallback, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { DialogV2, FileUploadDropzone } from 'c-components';
import type { TranslationPath } from 'c-translation';
import type { UseBoolean } from 'react-hanger';
import to from 'await-to-js';
import apiClient from 'c-data/apiClient';
import { useAPIClientRequest } from 'c-hooks';
import { errorMessageFromResponse } from 'c-data-layer';

type Props = {
    campaignId: number;
    dialogState: UseBoolean;
};

const DROP_LABEL = 'Drag & drop a CSV here, or click to browse' as TranslationPath;

// Uploads a single schedule CSV to POST campaign/:id/creative-validation and shows the
// returned message. We only ever use the default (live) environment here.
const CreativeScheduleCheckDialog: React.FC<Props> = ({ campaignId, dialogState }) => {
    const [file, setFile] = useState<File | null>(null);
    const [result, setResult] = useState<{ alert: string; warning?: string } | null>(null);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const { start, isLoading } = useAPIClientRequest(
        apiClient.Entities.Campaign.creativeValidation,
    );

    const reset = useCallback(() => {
        setFile(null);
        setResult(null);
        setErrorMsg(null);
    }, []);

    const close = useCallback(() => {
        reset();
        dialogState.setFalse();
    }, [reset, dialogState]);

    // Only a single CSV is used, even if more are dropped.
    const onFilesAdded = useCallback((files: File[]) => {
        setResult(null);
        setErrorMsg(null);
        setFile(files[0] ?? null);
    }, []);

    const onSubmit = useCallback(async () => {
        if (!file) return;
        setResult(null);
        setErrorMsg(null);
        const formData = new FormData();
        formData.append('schedule', file);
        const [err, res] = await to(start(campaignId, formData));
        if (err || !res?.data) {
            setErrorMsg(errorMessageFromResponse(err as any));
            return;
        }
        setResult({ alert: res.data.alert, warning: res.data.warning });
    }, [file, start, campaignId]);

    return (
        <DialogV2
            open={dialogState.value}
            onClose={close}
            title="Creative Schedule Check"
            maxWidth="sm"
        >
            <Stack gap={2}>
                <FileUploadDropzone
                    fileAccept=".csv,text/csv"
                    dropzoneLabel={DROP_LABEL}
                    onFilesAdded={onFilesAdded}
                    disabled={isLoading}
                />
                {file && (
                    <Typography variant="body2" noWrap>
                        Selected: {file.name}
                    </Typography>
                )}

                <Box>
                    <Button variant="contained" onClick={onSubmit} disabled={!file || isLoading}>
                        {isLoading ? <CircularProgress size={20} /> : 'Run check'}
                    </Button>
                </Box>

                {result && (
                    <Stack gap={1}>
                        <Alert severity="success">{result.alert}</Alert>
                        {result.warning && <Alert severity="warning">{result.warning}</Alert>}
                    </Stack>
                )}
                {errorMsg && <Alert severity="error">{errorMsg}</Alert>}
            </Stack>
        </DialogV2>
    );
};

export default CreativeScheduleCheckDialog;
